// Mock data for the Bookers (redesigned) landing page.
// All content lives here so backend integration is straightforward later.

import {
  CalendarClock, Clock3, LayoutGrid, BellRing, UserRound, ShieldCheck,
  BarChart3, Plug, Bot, Sparkles, MailCheck, TrendingUp, Smartphone,
  Zap, Utensils, Wine, PartyPopper, Building2, Hotel,
  Ban, Wallet, Receipt, Headphones, Globe, MessageCircle, Rocket, Share2
} from 'lucide-react';

export const IMAGES = {
  heroShowcase: 'https://images.unsplash.com/photo-1551632436-cbf8dd35adfa',
  serviceTable: 'https://images.pexels.com/photos/24433378/pexels-photo-24433378.jpeg',
  serviceExperience: 'https://images.unsplash.com/photo-1572715376701-98568319fd0b',
  serviceEvents: 'https://images.unsplash.com/photo-1663530761401-15eefb544889',
  solutionRestaurant: 'https://images.pexels.com/photos/7627408/pexels-photo-7627408.jpeg',
  solutionHotel: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0',
};

export const NAV_FEATURES = [
  {
    group: 'AI & Automation',
    items: [
      { icon: Bot, title: 'AI Virtual Host', desc: 'Handles queries, menus & orders automatically.' },
      { icon: Sparkles, title: 'Smart Upselling', desc: 'Suggests add-ons and upgrades to boost sales.' },
      { icon: MailCheck, title: 'Auto Confirmations', desc: 'Sends instant SMS and email booking alerts.' },
      { icon: TrendingUp, title: 'Predictive Analytics', desc: 'Tracks trends and guest behavior insights.' },
    ],
  },
  {
    group: 'Reservation Tools',
    items: [
      { icon: CalendarClock, title: '24/7 Online Booking', desc: 'Guests can reserve anytime, from anywhere.' },
      { icon: Clock3, title: 'Real-Time Availability', desc: 'Live table updates for efficient seating.' },
      { icon: LayoutGrid, title: 'Visual Table Planner', desc: 'Drag-and-drop tool for easy layout control.' },
      { icon: BellRing, title: 'Waitlist Management', desc: 'Notify guests via SMS when tables are ready.' },
    ],
  },
  {
    group: 'Operations & Integrations',
    items: [
      { icon: PartyPopper, title: 'Event Management', desc: 'Sell and manage pre-paid dining events.' },
      { icon: Plug, title: 'POS Integration', desc: 'Sync bookings with ChasPOS and other systems.' },
      { icon: Smartphone, title: 'Owner App', desc: 'Manage capacity and bookings from your mobile.' },
      { icon: BarChart3, title: 'CRM & Reporting', desc: 'Access insights to grow guest engagement.' },
    ],
  },
];

export const HERO_TRUST = [
  { icon: Plug, label: 'Easy Integration' },
  { icon: Smartphone, label: 'Mobile Ready' },
  { icon: Zap, label: 'Fast Setup' },
];

export const TOP_STATS = [
  { tag: 'Faster', value: '3x', suffix: '', note: 'more online bookings through automation.' },
  { tag: 'Smarter', value: '40', suffix: '%', note: 'fewer no-shows via reminders.' },
  { tag: 'Leaner', value: '65', suffix: '%', note: 'less time spent on the phone.' },
];

export const SERVICES = [
  {
    key: 'table', icon: Utensils, title: 'Book a Table', image: IMAGES.serviceTable,
    desc: 'Offer seamless online reservations with real-time availability, automated confirmations, and smart table management.',
  },
  {
    key: 'experience', icon: Wine, title: 'Experiences', image: IMAGES.serviceExperience,
    desc: 'From curated tastings to chef-driven journeys — discover unforgettable moments your guests will remember.',
  },
  {
    key: 'events', icon: PartyPopper, title: 'Events', image: IMAGES.serviceEvents,
    desc: 'Plan your next gathering with us — intimate, stylish, and perfectly organized from invite to last course.',
  },
];

export const FEATURES = [
  { icon: CalendarClock, title: '24/7 Booking', desc: 'Guests can book tables anytime with 24/7 online access.' },
  { icon: Clock3, title: 'Real-Time Availability', desc: 'See real-time table availability to manage seating efficiently.' },
  { icon: LayoutGrid, title: 'Visual Table Planner', desc: 'Drag and drop tables easily with the visual planner tool.' },
  { icon: BellRing, title: 'Auto Confirmations', desc: 'Automated emails and SMS reduce no-shows dramatically.' },
  { icon: UserRound, title: 'Guest Profiles', desc: 'Store guest preferences and history for personalized service.' },
  { icon: ShieldCheck, title: 'Secure Payments', desc: 'Securely collect deposits and payments before reservations.' },
  { icon: BarChart3, title: 'Analytics & CRM', desc: 'Reports help improve guest experience and sales.' },
  { icon: Plug, title: 'System Integrations', desc: 'Integrates smoothly with POS, PMS, and other key systems.' },
];

export const SOLUTIONS = [
  {
    key: 'restaurants', icon: Building2, title: 'For Restaurants', image: IMAGES.solutionRestaurant,
    lead: 'Simplify booking processes, enhance guest satisfaction, and optimize table management.',
    points: [
      { title: 'Effortless Booking Management', desc: '24/7 online reservations with real-time availability and a visual seating planner to maximize occupancy.' },
      { title: 'Reduce No-Shows', desc: 'Automated confirmations and reminders, plus payment integration to secure bookings.' },
      { title: 'Personalized Guest Experience', desc: 'Store guest preferences, track visit history, and engage diners with targeted service.' },
    ],
  },
  {
    key: 'hotels', icon: Hotel, title: 'For Hotels', image: IMAGES.solutionHotel,
    lead: 'Manage room reservations, events, and guest services all in one place.',
    points: [
      { title: 'Event Bookings', desc: 'Synchronize room and event reservations with an easy-to-use dashboard and real-time inventory.' },
      { title: 'Increase Revenue with Upselling', desc: 'Offer pre-paid services, add-ons, and special packages to maximize guest spending.' },
      { title: 'Guest Engagement & Loyalty', desc: 'Build relationships through personalized offers, loyalty programs, and automation.' },
    ],
  },
];

export const STEPS = [
  { n: '1', icon: Rocket, title: 'Sign up', desc: 'Create your Bookers account in minutes \u2014 no setup fees and no long-term contracts.' },
  { n: '2', icon: Smartphone, title: 'Download the app', desc: 'Get Bookers on iOS or Google Play and start receiving reservations instantly.' },
  { n: '3', icon: Share2, title: 'Embed or share', desc: 'Embed the reservation system directly inside your website, or share your dedicated your-store.wearebookers.com link on social media, Google Business, or by email.' },
  { n: '4', icon: MessageCircle, title: 'Manage on the go', desc: 'Take reservations directly on your mobile phone through the app \u2014 no hassle, wherever you are.' },
];

export const RESULTS = [
  { value: '3', suffix: 'x', note: 'more online bookings through automated tools.' },
  { value: '40', suffix: '%', note: 'fewer no-shows via confirmations and reminders.' },
  { value: '65', suffix: '%', note: 'less time spent on the phone.' },
  { value: '98', suffix: '%', note: 'user satisfaction from restaurant partners.' },
];

export const TESTIMONIALS = [
  { title: 'Smooth Setup!', quote: 'Simple setup, smooth performance, and great support. It is exactly what we needed to streamline our reservations.', name: 'Laura T.', role: 'Restaurant Manager' },
  { title: 'Highly recommend!', quote: 'Working with this platform has been a game-changer. The system is reliable, user-friendly, and perfectly supports our daily operations.', name: 'Sarah L.', role: 'Restaurant Owner' },
  { title: 'Excellence!', quote: 'The integration with our POS was seamless, and customer support is always quick to respond. Highly recommend!', name: 'James P.', role: 'Hospitality Manager' },
  { title: 'Impressive!', quote: 'Our bookings have doubled since using this system. The real-time availability feature keeps everything organized.', name: 'Priya K.', role: 'Cafe Partner' },
];

export const FEATURE_TESTIMONIALS = [
  {
    name: 'Marco Rossi', company: 'Osteria del Ponte', role: 'Founder & Head Chef',
    quote: 'What used to take my team hours every night now happens automatically. Bookers gave us back our evenings — and doubled our covers.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODQwNTMwMDZ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'Elena Fischer', company: 'Maison Lumiere', role: 'General Manager',
    quote: 'Finally a reservation system that actually uses smart automation: less manual work, more full tables, and happier guests.',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODQwNTMwMDZ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    name: 'David Chen', company: 'Harbor & Vine', role: 'Owner',
    quote: 'The development pace is remarkable. We believed in Bookers from day one, and it is paying off at scale across all three of our venues.',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHBlcnNvbnxlbnwwfHx8fDE3ODQxNTI5MjF8MA&ixlib=rb-4.1.0&q=85',
  },
];

export const PACKAGES = [
  {
    name: 'Tables',
    price: '39',
    original: '49',
    offer: 'Launch offer',
    tag: 'Introduction price',
    popular: false,
    desc: 'Everything you need to take table reservations.',
    features: [
      'Dedicated your-store.wearebookers.com page',
      'Embed the reservation system on your website',
      '24/7 online reservations',
      'Real-time availability & table planner',
      'Automated confirmations & reminders',
      'Deposits & no-show protection',
      'iOS + Android app',
      'Reporting & analytics',
    ],
  },
  {
    name: 'Tables + Experiences',
    price: '89',
    original: '109',
    offer: 'Launch offer',
    tag: 'Most popular',
    popular: true,
    desc: 'Add curated experiences, tastings and chef menus.',
    features: [
      'Everything in Tables',
      'Curated experiences & tastings',
      'Prepaid experience bookings',
      'Smart upselling & add-ons',
      'Advanced CRM & guest profiles',
    ],
  },
  {
    name: 'Full Suite',
    price: '119',
    original: '149',
    offer: 'Launch offer',
    tag: 'Introduction price',
    popular: false,
    desc: 'Tables, experiences and full event management.',
    features: [
      'Everything in Experiences',
      'Full event management',
      'Advance payments for events',
      'Ticketing & capacity control',
      'Priority onboarding & support',
    ],
  },
];

export const FREE_TRIAL = {
  eyebrow: 'Starting a new business?',
  title: 'Just launching? Bookers is free to begin.',
  text: 'If you are opening a new venue, use Bookers completely free until you reach 150 reservations per month. No credit card \u2014 pay only when you grow.',
  highlightValue: '150',
  highlightLabel: 'free reservations / month',
  cta: 'Start for free',
};

export const PAYMENT_CONTROLS = [
  { icon: Ban, title: 'Penalize no-shows', desc: 'Automatically charge or flag guests who do not show up \u2014 protect every cover.' },
  { icon: Wallet, title: 'Deposits for tables', desc: 'Set refundable deposits on table reservations to secure every booking.' },
  { icon: Receipt, title: 'Advance payments for events', desc: 'Collect advance payments and ticket fees for events, upfront and automatically.' },
];

export const MODULES = [
  { key: 'tables', label: 'Tables', desc: 'Reservations & seating', icon: Utensils },
  { key: 'experiences', label: 'Experiences', desc: 'Tastings & chef menus', icon: Wine },
  { key: 'events', label: 'Events', desc: 'Ticketed & prepaid events', icon: PartyPopper },
];

export const SUPPORT = {
  eyebrow: 'Support',
  title: '24/7 support from Neuch\u00e2tel',
  text: 'Real people, based in Switzerland. Our team is here around the clock \u2014 and we speak your language.',
  languages: ['Fran\u00e7ais', 'Deutsch', 'Italiano', 'English'],
  stats: [
    { icon: Headphones, value: '24/7', note: 'Support availability' },
    { icon: Globe, value: '4', note: 'Languages spoken' },
    { icon: MessageCircle, value: '<5 min', note: 'Average response time' },
  ],
};

export const FAQS = [
  { q: 'What is Bookers and what can I use it for?', a: 'Bookers is a cloud-based smart reservation system for restaurants and hospitality venues. It lets you centrally manage tables, experiences, events, guests, payments and reporting — all from one platform.' },
  { q: 'Is Bookers free?', a: 'Yes, 100% free. No credit card required. New venues get up to 150 free reservations per month — you only pay when you grow.' },
  { q: 'Which businesses is Bookers suited for?', a: 'Bookers is ideal for restaurants, cafes, bars, hotels and hospitality groups of any size — from a single venue to multi-location operations.' },
  { q: 'Do I need technical knowledge to use Bookers?', a: 'No. Bookers is built so that anyone on your team can manage bookings and tables efficiently, without any technical or setup expertise.' },
  { q: 'Is Bookers cloud-based?', a: 'Yes. You can access Bookers anytime and from anywhere through your browser or the mobile owner app.' },
  { q: 'Does Bookers integrate with my POS?', a: 'Yes. Bookers syncs smoothly with ChasPOS and other major POS and PMS systems so your bookings and floor stay in sync.' },
  { q: 'Can I manage multiple venues in one account?', a: 'Absolutely. Bookers lets you manage several restaurants or locations within a single account and dashboard.' },
];

export const LOGOS = ['Osteria', 'Lumiere', 'Harbor & Vine', 'The Grove', 'Saffron', 'Bluefin', 'Cellar 8', 'Nordic'];
